import { useEffect, useState } from 'react'
import { Stack, Typography, Backdrop } from '@mui/material'
import { FormattedMessage, useIntl } from 'react-intl'
import { useMutation } from '@tanstack/react-query'
import LicensePlateInput from '../../components/licensePlateInput'
import { ConfigDTO, Organization, Product } from '../../apis/configuration/dto'
import { MoovyToast } from '../../utils/moovyToast'
import HeaderContent from './headerContent'
import ProductContent from './productContent'
import SuccessPage from './successPage'
import { SelectChangeEvent } from '@mui/material/Select'
import MoovyProgress from '../../components/moovyProgress'

import { useParams } from 'react-router'
import i18n from '../../i18n'
import { ApiAxiosError } from '../../apis/ApiError'
import { GrantedVoucherDTO } from '../../apis/permits/dto'
import permitsAPI from '../../apis/permits/permitsAPI'

function formatValidity(from: string, to: string): string {
  const now = new Date(from)
  const end = new Date(to)

  const dateFormatter = new Intl.DateTimeFormat('fi-FI', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })

  const timeFormatter = new Intl.DateTimeFormat('fi-FI', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })

  const startDate = dateFormatter.format(now)
  const endDate = dateFormatter.format(end)

  const startTime = timeFormatter.format(now)
  const endTime = timeFormatter.format(end)

  // Show end date only if it's different from start date
  if (startDate !== endDate) {
    return `${startDate} ${startTime} - ${endDate} ${endTime}`
  }

  return `${startDate} ${startTime} - ${endTime}`
}

const HomePage = ({ configuration }: { configuration: ConfigDTO }) => {
  const [showSuccess, setShowSuccess] = useState(false)
  const [selectedOrganizationId, setSelectedOrganizationId] =
    useState<string>('')
  const [selectedProductId, setSelectedProductId] = useState<string | ''>('')
  const [licensePlate, setLicensePlate] = useState<string | ''>('')
  const [productError, setProductError] = useState<boolean>(false)
  const intl = useIntl()

  useEffect(() => {
    if (configuration.organizations.length === 1) {
      const firstOrganization = configuration.organizations[0]
      setSelectedOrganizationId(firstOrganization.id)
      if (firstOrganization.products.length === 1)
        setSelectedProductId(firstOrganization.products[0].id)
    }
  }, [showSuccess])

  const organizations: Organization[] = configuration.organizations
  const location: { name: string } = configuration.permitOffering

  const storedAppSettings = configuration.appSettings

  const useMoovyKeyboard: boolean = storedAppSettings.virtualKeyboard ?? false

  const { guid } = useParams()

  const handleProductChange = (event: SelectChangeEvent<string>) => {
    setSelectedProductId(event.target.value)
    setProductError(false)
  }

  const validateSelection = (): boolean => {
    if (!selectedProductId) {
      setProductError(true)
      return false
    }

    return true
  }

  const selectedOrg = organizations.find(
    (organization) => organization.id === selectedOrganizationId
  )
  const availableProducts: Product[] = selectedOrg?.products || []

  const createPermitMutation = useMutation<GrantedVoucherDTO>({
    mutationFn: () =>
      permitsAPI.createPermit(guid, licensePlate, selectedProductId),
    onSuccess: () => {
      setShowSuccess(true)
    },
    onError: (error: unknown) => {
      const typedError = error as ApiAxiosError
      type Language = 'fi' | 'en'
      const language = i18n.language as Language
      const errorDescription =
        typedError.response?.data?.errorDescriptor?.errorDescription
      if (errorDescription?.[language]) {
        MoovyToast.showToast(
          <FormattedMessage
            id="page.homePage.toast.errorCreatingPermit.localizedErrorMessage"
            values={{ message: errorDescription[language] }}
          />,
          false
        )
      } else {
        MoovyToast.showToast(
          <FormattedMessage id="page.homePage.toast.errorCreatingPermit" />,
          false
        )
      }
    }
  })

  const handleRedeem = () => {
    if (createPermitMutation.isPending || !validateSelection()) {
      return
    }

    createPermitMutation.mutate()
  }

  const handleSuccessClose = () => {
    setShowSuccess(false)
    setSelectedOrganizationId('')
    setSelectedProductId('')
    setLicensePlate('')
  }

  if (showSuccess) {
    const permitTime =
      createPermitMutation.data?.validityFrom &&
      createPermitMutation.data?.validityTo
        ? formatValidity(
            createPermitMutation.data.validityFrom,
            createPermitMutation.data.validityTo
          )
        : '-'
    return (
      <SuccessPage
        locationName={location.name}
        licensePlate={licensePlate}
        onClose={handleSuccessClose}
        permitTime={permitTime}
      />
    )
  }

  return (
    <>
      <Backdrop
        open={createPermitMutation.isPending}
        sx={{
          zIndex: 9999 // Korkein z-index, jotta tulee kaiken päälle
        }}
      >
        <MoovyProgress
          size={60}
          success={
            !createPermitMutation.isPending && !createPermitMutation.isError
          }
        />
      </Backdrop>

      <Stack
        direction="column"
        spacing={1}
        sx={{
          justifyContent: 'space-evenly',
          alignItems: 'center',
          marginTop: 10
        }}
      >
        <HeaderContent
          storedLocation={location}
          useMoovyKeyboard={useMoovyKeyboard}
        />
        <ProductContent
          availableProducts={availableProducts}
          selectedProduct={selectedProductId}
          handleProductChange={handleProductChange}
          productError={productError}
        />
        <Typography
          variant="h2"
          aria-label={intl.formatMessage({
            id: 'page.homePage.instructions.registerNumber.arialabel'
          })}
          sx={{
            color: 'text.primary',
            textAlign: 'center',
            mt: 2,
            mb: 1
          }}
        >
          <FormattedMessage id="page.homePage.instructions.enterRegisterNumber" />
        </Typography>
        <LicensePlateInput
          onRedeem={handleRedeem}
          useMoovyKeyboard={useMoovyKeyboard}
          value={licensePlate}
          onChange={(value) => {
            setLicensePlate(value)
          }}
        />
      </Stack>
    </>
  )
}

export default HomePage
